/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { Ref } from 'preact';
import { forwardRef, ElementRef } from 'preact/compat';
import { useCallback, useImperativeHandle, useRef } from 'preact/hooks';
import { type BundleType } from '../resources/nls/bundle';
import { type ColorValue, isValidColorValue } from '#utils/UNSAFE_color';
import { InputColorPickerDropdown } from './InputColorPickerDropdown';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { ValueUpdateDetail } from '#utils/UNSAFE_valueUpdateDetail';
import { ColorSwatch } from '#UNSAFE_ColorSwatch';
import { useInputColor } from './useInputColor';

import { useClearIcon } from '../hooks/UNSAFE_useClearIcon';
import { FocusableHandle, useFocusableTextField } from '../hooks/UNSAFE_useFocusableTextField';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { FormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useHover } from '../hooks/UNSAFE_useHover';
import { useTextField } from '../hooks/UNSAFE_useTextField';
import { Label } from '../UNSAFE_Label';

import {
  ReadonlyTextField,
  ReadonlyTextFieldInput,
  TextField,
  TextFieldInput
} from '../UNSAFE_TextField';
import { CompactUserAssistance, InlineUserAssistance } from '../UNSAFE_UserAssistance';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { ClearIcon } from '../PRIVATE_ClearIcon/ClearIcon';
import { useCurrentValueReducer } from '../hooks/UNSAFE_useCurrentValueReducer';
import { SelectableHandle, useSelectableTextField } from '#hooks/UNSAFE_useSelectableTextField';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { InputColorPublicProps as Props } from './InputColor.types';

type SwatchHandle = ElementRef<typeof ColorSwatch>;

type FocusSelectHandle = FocusableHandle & SelectableHandle;

/**
 * An Input Color is a form field that allows users to select or input a color value,
 * using a color picker or by enterihg a HEX code.
 */
export const InputColor = forwardRef(
  (
    {
      'aria-describedby': ariaDescribedBy,
      swatchSize,
      palette,
      value,
      isDisabled: propIsDisabled,
      isReadonly: propIsReadonly,
      labelEdge: propLabelEdge,
      labelStartWidth: propLabelStartWidth,
      isRequiredShown = false,
      isRequired = false,
      placeholder,
      hasClearIcon,
      columnSpan,
      textAlign: propTextAlign,
      autoComplete,
      messages,
      assistiveText,
      helpSourceLink,
      helpSourceText,
      label,
      onCommit,
      onInput,
      testId,
      variant,
      userAssistanceDensity: propUserAssistanceDensity
    }: Props,
    ref?: Ref<FocusSelectHandle>
  ) => {
    const {
      colorIconActionHandler,
      colorPickerCommitHandler,
      dropdownCloseHandler,
      colorValue,
      containerDropdownProps,
      iconRef,
      isOpen,
      colorCommitHandler,
      colorInputHandler,
      colorRef: enabledElementRef
    } = useInputColor({
      onCommit,
      onInput,
      value
    });

    const mainFieldRef = useRef<HTMLDivElement>(null);
    const { inputColor_selectColor } = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
    const colorIconAriaLabel = inputColor_selectColor();
    const { onKeyDown, ...inputContainerAriaProps } = containerDropdownProps;

    const { currentCommitValue, dispatch } = useCurrentValueReducer({ value: colorValue });
    const onInputAndDispatch = useCallback(
      (detail: ValueUpdateDetail<ColorValue>) => {
        // Should dispatch happen first? This will queue up a re-render, ordering should not cause issues (this is async)
        dispatch({ type: 'input', payload: detail.value });
        colorInputHandler?.(detail);
      },
      [colorInputHandler, dispatch]
    );
    const onCommitAndDispatch = useCallback(
      (detail: ValueUpdateDetail<ColorValue>) => {
        // Should dispatch happen first? This will queue up a re-render, ordering should not cause issues (this is async)
        dispatch({ type: 'commit', payload: detail.value });
        colorCommitHandler?.(detail);
      },
      [colorCommitHandler, dispatch]
    );

    const startContent = () => (
      <ColorSwatch
        size="xs"
        aria-haspopup={palette ? 'grid' : undefined} // with only palette, grid is a better choice than dialog for now.
        aria-label={palette ? colorIconAriaLabel : undefined}
        isDisabled={isDisabled}
        isButton={true}
        isTooltipDisabled={isOpen}
        ref={iconRef as Ref<SwatchHandle>}
        testId={testId ? testId + '_colorIcon' : undefined}
        value={currentCommitValue as ColorValue}
        onToggle={isDisabled || !palette ? undefined : colorIconActionHandler}
      />
    );
    const {
      isDisabled: isFormDisabled,
      isReadonly: isFormReadonly,
      labelEdge: formLabelEdge,
      labelStartWidth: formLabelStartWidth,
      textAlign: formTextAlign,
      userAssistanceDensity: formUserAssistanceDensity
    } = useFormContext();
    // default to FormContext values if component properties are not specified
    const isDisabled = propIsDisabled ?? isFormDisabled;
    const isReadonly = propIsReadonly ?? isFormReadonly;
    const labelEdge = propLabelEdge ?? formLabelEdge;
    const labelStartWidth = propLabelStartWidth ?? formLabelStartWidth;
    const textAlign = propTextAlign ?? formTextAlign;
    const userAssistanceDensity = propUserAssistanceDensity ?? formUserAssistanceDensity;

    const readonlyElementRef = useRef<HTMLDivElement>(null);

    const {
      focusProps,
      isFocused,
      methods: focusMethods
    } = useFocusableTextField<HTMLInputElement, HTMLDivElement>({
      isDisabled,
      isReadonly,
      enabledElementRef,
      readonlyElementRef
    });

    const { methods: selectMethods } = useSelectableTextField<HTMLInputElement>(enabledElementRef);

    useImperativeHandle(ref!, () => mergeProps(focusMethods, selectMethods), [
      focusMethods,
      selectMethods
    ]);

    const { hoverProps, isHover } = useHover({ isDisabled: isReadonly || isDisabled || false });

    const { formFieldContext, inputProps, labelProps, textFieldProps, userAssistanceProps } =
      useTextField({
        ariaDescribedBy,
        helpSourceLink,
        helpSourceText,
        isDisabled,
        isFocused: isFocused || isOpen,
        isReadonly,
        isRequiredShown,
        labelEdge,
        messages,
        styleVariant: variant,
        userAssistanceDensity,
        value: colorValue
      });

    const onClickClearIcon = useCallback(() => {
      // Clicking the clear icon should put the focus on the input field
      enabledElementRef.current?.focus();
      // Send an event to clear the field's value if the value is not already cleared
      const nextValue = undefined;
      if (colorValue === nextValue) return;
      onInputAndDispatch?.({ previousValue: colorValue, value: nextValue });
    }, [onInputAndDispatch, colorValue, enabledElementRef]);

    const maybeClearIcon = useClearIcon({
      clearIcon: <ClearIcon onClick={onClickClearIcon} testId={`${testId}_clearicon`} />,
      display: hasClearIcon,
      hasValue: formFieldContext.hasValue,
      isFocused,
      isEnabled: !isReadonly && !isDisabled,
      isHover
    });

    const hasInsideLabel = label !== undefined && labelEdge === 'inside';

    const labelComp = labelEdge !== 'none' ? <Label {...labelProps}>{label}</Label> : undefined;

    const fieldLabelProps = {
      label: labelEdge !== 'none' ? labelComp : undefined,
      labelEdge: labelEdge !== 'none' ? labelEdge : undefined,
      labelStartWidth: labelEdge !== 'none' ? labelStartWidth : undefined
    };

    const ariaLabel = labelEdge === 'none' ? label : undefined;
    const isInlineDensity =
      userAssistanceDensity === 'efficient' || userAssistanceDensity === 'reflow';

    const inlineUserAssistance = isInlineDensity ? (
      isDisabled || isReadonly ? (
        // save space for user assistance if density is 'efficient', even though we don't
        // render user assistance for disabled or readonly fields
        userAssistanceDensity !== 'efficient' ? undefined : (
          <InlineUserAssistance
            userAssistanceDensity={userAssistanceDensity}
            {...userAssistanceProps}
          />
        )
      ) : (
        <InlineUserAssistance
          assistiveText={assistiveText}
          fieldLabel={label}
          helpSourceLink={helpSourceLink}
          helpSourceText={helpSourceText}
          messages={messages}
          isRequiredShown={isRequiredShown}
          userAssistanceDensity={userAssistanceDensity}
          {...userAssistanceProps}
        />
      )
    ) : undefined;

    const aRef = useRef<HTMLDivElement>(null);
    const anchorRef = mainFieldRef ?? aRef;

    const compactUserAssistance =
      userAssistanceDensity === 'compact' ? (
        <CompactUserAssistance
          anchorRef={anchorRef}
          messages={messages}
          assistiveText={assistiveText}
          {...userAssistanceProps}
        />
      ) : undefined;

    const mainContent = isReadonly ? (
      <ReadonlyTextFieldInput
        aria-describedby={inputProps['aria-describedby']}
        aria-label={ariaLabel}
        aria-labelledby={labelProps.id}
        as="div"
        elementRef={readonlyElementRef}
        textAlign={textAlign}
        value={colorValue}
        hasEmptyLabel={label === '' && labelEdge === 'none'}
        hasInsideLabel={hasInsideLabel}
      />
    ) : (
      <TextFieldInput
        aria-labelledby={labelProps.id}
        aria-label={ariaLabel}
        autoComplete={autoComplete}
        currentCommitValue={currentCommitValue}
        hasEmptyLabel={label === '' && labelEdge === 'none'}
        hasEndContent={false}
        hasInsideLabel={labelComp !== undefined && labelEdge === 'inside'}
        hasStartContent={true}
        inputRef={enabledElementRef}
        isRequired={isRequired}
        placeholder={placeholder}
        textAlign={textAlign}
        type={isMobile() ? 'text' : undefined}
        value={colorValue}
        onCommit={
          onCommitAndDispatch as (detail: ValueUpdateDetail<string>) => {
            value: string;
          }
        }
        onInput={
          onInputAndDispatch as (detail: ValueUpdateDetail<string>) => {
            value: string;
          }
        }
        onKeyDown={onKeyDown}
        {...inputProps}
      />
    );

    return (
      <>
        <FormFieldContext.Provider value={formFieldContext}>
          {isReadonly ? (
            <ReadonlyTextField
              role="presentation"
              columnSpan={columnSpan}
              compactUserAssistance={compactUserAssistance}
              inlineUserAssistance={inlineUserAssistance}
              onBlur={focusProps.onFocusOut}
              onFocus={focusProps.onFocusIn}
              ref={anchorRef}
              mainContent={mainContent}
              testId={testId}
              {...fieldLabelProps}></ReadonlyTextField>
          ) : (
            <TextField
              endContent={maybeClearIcon}
              columnSpan={columnSpan}
              compactUserAssistance={compactUserAssistance}
              inlineUserAssistance={inlineUserAssistance}
              mainContent={mainContent}
              onBlur={focusProps.onFocusOut}
              onFocus={focusProps.onFocusIn}
              startContent={startContent()}
              mainFieldRef={anchorRef}
              hasZeroStartMargin={true}
              testId={testId}
              {...textFieldProps}
              {...fieldLabelProps}
              {...hoverProps}
              {...inputContainerAriaProps}
            />
          )}
        </FormFieldContext.Provider>

        {isOpen && !isReadonly && !isDisabled && (
          <InputColorPickerDropdown
            anchorRef={mainFieldRef}
            assistiveText={assistiveText}
            helpSourceLink={helpSourceLink}
            helpSourceText={helpSourceText}
            isDisabled={isDisabled}
            isReadonly={isReadonly}
            isOpen={isOpen}
            label={label}
            onClose={dropdownCloseHandler}
            onCommit={colorPickerCommitHandler}
            messages={messages}
            testId={testId ? testId + '_colorPicker' : undefined}
            userAssistanceDensity={userAssistanceDensity}
            // memoize this for performance
            value={colorValue && isValidColorValue(colorValue) ? colorValue : undefined}
            palette={palette}
            swatchSize={swatchSize}
          />
        )}
      </>
    );
  }
);

/**
 * Helper function to determine whether the current device is a mobile device
 * @returns true if running on a mobile device, false otherwise
 */
function isMobile() {
  const deviceType = getClientHints().deviceType;
  return deviceType === 'phone' || deviceType === 'tablet';
}
InputColor.displayName = 'InputColor';
